import { LogIn, LogOut, Coffee, Clock } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { BREAK_OPTIONS, type BreakType, type TodayResponse } from '../types';

interface MyAttendanceCardProps {
  today?: TodayResponse;
  loading: boolean;
  busy: boolean;
  onCheckIn: () => void;
  onCheckOut: () => void;
  onStartBreak: (type: BreakType) => void;
  onEndBreak: () => void;
}

export function MyAttendanceCard({
  today,
  loading,
  busy,
  onCheckIn,
  onCheckOut,
  onStartBreak,
  onEndBreak,
}: MyAttendanceCardProps) {
  const log = today?.log ?? null;
  const activeBreak = today?.activeBreak ?? null;
  const checkedIn = !!log?.check_in;
  const checkedOut = !!log?.check_out;
  const onBreak = !!activeBreak;

  return (
    <Card className="p-5">
      {loading ? (
        <div className="flex h-28 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <h2 className="text-data-lg font-semibold text-on-surface">My Attendance — Today</h2>
            </div>
            {checkedOut ? (
              <Badge tone="info">Checked Out</Badge>
            ) : checkedIn ? (
              <Badge tone="success">Checked In</Badge>
            ) : (
              <Badge tone="neutral">Not Checked In</Badge>
            )}
          </div>

          {/* Times */}
          <div className="grid grid-cols-3 gap-3">
            <Metric label="Check In" value={log?.check_in || '—'} />
            <Metric label="Check Out" value={log?.check_out || '—'} />
            <Metric label="Hours" value={log?.hours != null && log.hours > 0 ? `${log.hours}h` : '—'} />
          </div>

          {onBreak && (
            <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm text-on-surface">
              <Coffee className="h-4 w-4 text-warning-text" />
              On break ({activeBreak?.break_type})
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 border-t border-outline pt-4">
            {!checkedIn && (
              <Button onClick={onCheckIn} loading={busy}>
                <LogIn className="h-4 w-4" /> Check In
              </Button>
            )}

            {checkedIn && !checkedOut && (
              <>
                {onBreak ? (
                  <Button variant="secondary" onClick={onEndBreak} loading={busy}>
                    <Coffee className="h-4 w-4" /> End Break
                  </Button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="text-body-sm text-on-surface-variant">Break:</span>
                    {BREAK_OPTIONS.map((b) => (
                      <Button
                        key={b.value}
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => onStartBreak(b.value)}
                      >
                        {b.label}
                      </Button>
                    ))}
                  </div>
                )}
                <Button variant="danger" onClick={onCheckOut} loading={busy} disabled={onBreak}>
                  <LogOut className="h-4 w-4" /> Check Out
                </Button>
              </>
            )}

            {checkedOut && (
              <p className="text-body-sm text-on-surface-variant">You're done for today. See you tomorrow!</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card-sm border border-outline bg-surface-container-low px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-on-surface-variant">{label}</p>
      <p className="text-data-lg font-semibold text-on-surface">{value}</p>
    </div>
  );
}
